import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCalendarAlt, FaUser, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import axios from 'axios';
import ScrollToTop from '../scrollToTop/ScrollToTop';

const News = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const postsPerPage = 6;

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/news`,
          {
            withCredentials: true, // if you use cookies/token

          }
        );
        setPosts(response.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch news');
        setPosts([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Pagination calculations
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(posts.length / postsPerPage);
  const pageLimit = 3;
  const startPage = Math.floor((currentPage - 1) / pageLimit) * pageLimit + 1;
  const endPage = Math.min(startPage + pageLimit - 1, totalPages);

  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const changePage = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-4 p-4 rounded-lg shadow-md bg-gray-100"
        >
          <FaSpinner className="animate-spin text-4xl text-indigo-600" />
          <h2 className="text-xl font-semibold text-indigo-600">
           Bishoftu Finance Office
          </h2>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop/>
      {/* Hero Section - Consistent with other pages */}
     

      {/* News Grid Section */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          {error ? (
            <div className="text-center py-12">
              <div className="bg-red-50 text-red-600 p-4 rounded-lg inline-block">
                {error}
              </div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-indigo-50 text-indigo-600 p-4 rounded-lg inline-block">
                No news articles available at the moment
              </div>
            </div>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-12 text-center"
              >
                <h2 className="text-2xl font-serif font-semibold text-gray-700 mb-2">Recent Articles</h2>
                <div className="w-20 h-1 bg-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Browse through our collection of financial news and updates
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {currentPosts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  >
                    {/* Image Container */}
                    <div className="relative h-48 bg-gray-100">
                      <Link to={`/news/details/${post.id}`} className="block w-full h-full">
                        {post.image ? (
                          <motion.img
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4 }}
                            alt={post.title}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            src={`${process.env.REACT_APP_API_URL}/${post.image}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        {/* Fallback Content */}
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`flex items-center justify-center p-4 bg-indigo-100 text-indigo-800 w-full h-full ${
                            post.image ? 'hidden' : 'flex'
                          }`}
                        >
                          <span className="text-lg font-medium">Bishoftu Finance</span>
                        </motion.div>
                      </Link>
                      {/* Date Badge */}
                      <motion.div 
                        initial={{ scale: 0.8 }}
                        whileInView={{ scale: 1 }}
                        className="absolute bottom-4 left-4 bg-indigo-600 text-white px-3 py-1 text-xs rounded-full flex items-center shadow-md"
                      >
                        <FaCalendarAlt className="mr-1" /> 
                        {new Date(post.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </motion.div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <FaUser className="mr-2" /> By {post.author || 'Finance Office'}
                      </div>
                      <h3 className="text-xl font-semibold mb-3 hover:text-indigo-600 transition-colors">
                        <Link to={`/news/details/${post.id}`}>
                          {post.title || 'Untitled Article'}
                        </Link>
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3"
                                        dangerouslySetInnerHTML={{ __html: post.description }} 
>
                        {/* {post.description || 'No description available'} */}
                      </p>
                      
                      <motion.div whileHover={{ x: 5 }} className="inline-block">
                        <Link 
                          to={`/news/details/${post.id}`}
                          className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                        >
                          Read More <FaArrowRight className="ml-2" />
                        </Link>
                      </motion.div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  className="mt-12"
                >
                  <nav className="flex justify-center">
                    <ul className="inline-flex items-center -space-x-px">
                      <li>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={prevPage}
                          disabled={currentPage === 1}
                          className={`px-4 py-2 ml-0 rounded-l-lg border text-sm font-medium ${
                            currentPage === 1 
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                              : 'bg-white text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          Previous
                        </motion.button>
                      </li>
                      
                      {startPage > 1 && (
                        <li>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => changePage(1)}
                            className="px-4 py-2 border text-sm font-medium bg-white text-gray-600 hover:bg-gray-50"
                          >
                            1
                          </motion.button>
                        </li>
                      )}
                      
                      {pageNumbers.map((pageNumber) => (
                        <li key={pageNumber}>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => changePage(pageNumber)}
                            className={`px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNumber 
                                ? 'bg-indigo-600 text-white border-indigo-600' 
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </motion.button>
                        </li>
                      ))}
                      
                      {endPage < totalPages && (
                        <li>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => changePage(totalPages)}
                            className="px-4 py-2 border text-sm font-medium bg-white text-gray-600 hover:bg-gray-50"
                          >
                            {totalPages}
                          </motion.button>
                        </li>
                      )}
                      
                      <li>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={nextPage}
                          disabled={currentPage === totalPages}
                          className={`px-4 py-2 rounded-r-lg border text-sm font-medium ${
                            currentPage === totalPages 
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                              : 'bg-white text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          Next
                        </motion.button>
                      </li>
                    </ul>
                  </nav>
                </motion.div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default News;